import { useCallback, useLayoutEffect, useRef, useState, type ReactElement, type Ref } from 'react';
import { Plus, Filter, ArrowDownUp, Sparkles, AtSign, Circle, Users, Image as ImageIcon, Check, ListFilter } from 'lucide-react';
import { Badge, Button, Menu, MenuItem, Popover, Switch, Tooltip, cn } from '@vd/ui';
import type { Column, ProjectTicketType, ProjectViewPrefs } from '@vd/types';
import { FilterModal, type FilterState } from '../modals/FilterModal';
import styles from './BoardToolbar.module.css';

export interface BoardToolbarProps {
  /** The row of controls, for the board to measure against its bulk bar. */
  ref?: Ref<HTMLDivElement>;
  prefs: ProjectViewPrefs;
  patch: (p: Partial<ProjectViewPrefs>) => void;
  onCreateCapture?: () => void;
  /** Optional node rendered right after "New ticket" — typically the view toggle. */
  viewToggle?: React.ReactNode;
  /** Project columns, forwarded into the filter modal so users can
   * filter by To Do / In Review / etc. */
  columns?: Column[];
  /** The project's resolved type set, forwarded into the filter modal (TOADO-411). */
  ticketTypes?: ProjectTicketType[];
}

const SORT_OPTIONS: Array<{ key: ProjectViewPrefs['sort']; label: string; short: string }> = [
  // 'user-sorted' is first because it's the default + the only mode that
  // preserves drag-and-drop order on the kanban. The others apply an
  // explicit sort within each column that overrides drag-within-column.
  { key: 'user-sorted',   label: 'User sorted',    short: 'Manual' },
  { key: 'newest',        label: 'Newest first',   short: 'Newest' },
  { key: 'oldest',        label: 'Oldest first',   short: 'Oldest' },
  { key: 'last-activity', label: 'Last activity',  short: 'Activity' },
  { key: 'most-comments', label: 'Most comments',  short: 'Comments' },
];

// Note: an "Archived" chip used to live here. Archived tickets have
// their own top-level page (`/archive`) reachable from the project
// toolbar above, so the in-toolbar chip was redundant + confusing
// (toggling it filtered the current view to include archived items
// alongside live ones, while the dedicated page shows them on their
// own). The `archived` flag still exists on ProjectViewPrefs and in
// FilterModal so older saved prefs and the deeper filter UI keep
// working; only the inline chip is gone. "Capture partial" is likewise in
// Filter only, and "Mentions me" is back in the row (Jason, 10 Oct 2026).
const CHIPS: Array<{ key: keyof ProjectViewPrefs['quickFilters']; label: string; does: string; icon: ReactElement }> = [
  { key: 'hasAiActivity',   label: 'Has AI activity', does: 'Shows only tickets an AI agent has commented on.', icon: <Sparkles size={11} /> },
  { key: 'unresolved',      label: 'Unresolved',      does: 'Shows only tickets not marked resolved.', icon: <Circle size={11} /> },
  { key: 'mentionsMe',      label: 'Mentions me',     does: 'Shows only tickets that @mention you.', icon: <AtSign size={11} /> },
  { key: 'externalReports', label: 'From customers',  does: 'Shows only reports sent through your widget or public report link.', icon: <Users size={11} /> },
];

/**
 * How the quick filters fit the toolbar's spare room (Jason, 10 Oct 2026):
 * named in full when they fit, as icons with a tooltip when they would be
 * clipped, and folded into one menu only when even the icons would be.
 */
type ChipsFit = 'full' | 'icons' | 'menu';

export function BoardToolbar({ ref, prefs, patch, onCreateCapture, viewToggle, columns, ticketTypes }: BoardToolbarProps) {
  const [sortAnchor, setSortAnchor] = useState<DOMRect | null>(null);
  const [chipsAnchor, setChipsAnchor] = useState<DOMRect | null>(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const sortBtn = useRef<HTMLButtonElement>(null);
  const chipsBtn = useRef<HTMLButtonElement>(null);
  const chipsFit = useChipsFit();

  const sortLabel = SORT_OPTIONS.find((s) => s.key === prefs.sort)?.short ?? 'Sort';
  const quickCount = Object.values(prefs.quickFilters).filter(Boolean).length;
  // The collapsed button counts what its menu holds, not filters set in Filter.
  const chipCount = CHIPS.filter((c) => prefs.quickFilters[c.key]).length;
  const assigneeCount = (prefs.assigneeIds?.length ?? 0) + (prefs.authorIds?.length ?? 0);
  const columnCount = prefs.columnIds?.length ?? 0;
  const dateCount = (prefs.createdFrom ? 1 : 0) + (prefs.createdTo ? 1 : 0);
  const typeCount = prefs.typeIds?.length ?? 0;
  const activeFilterCount = quickCount + assigneeCount + columnCount + dateCount + typeCount;

  const filterValue: FilterState = {
    quickFilters: prefs.quickFilters,
    assigneeIds: prefs.assigneeIds ?? [],
    authorIds: prefs.authorIds ?? [],
    columnIds: prefs.columnIds ?? [],
    createdFrom: prefs.createdFrom ?? '',
    createdTo: prefs.createdTo ?? '',
    typeIds: prefs.typeIds ?? [],
  };

  const toggleChip = (key: keyof ProjectViewPrefs['quickFilters']) => {
    patch({ quickFilters: { ...prefs.quickFilters, [key]: !prefs.quickFilters[key] } });
  };

  return (
    <div ref={ref} className={styles.toolbar}>
      <Button variant="primary" size="sm" leftIcon={<Plus size={14} />} onClick={onCreateCapture}>
        New ticket
      </Button>

      {viewToggle}

      <Button
        variant="secondary"
        size="sm"
        leftIcon={<Filter size={14} />}
        onClick={() => setFilterOpen(true)}
        data-tour-anchor="board.filter"
      >
        Filter
        {activeFilterCount > 0 && (
          <Badge variant="solid" style={{ height: 16, padding: '0 5px', fontSize: 10, marginLeft: 4 }}>
            {activeFilterCount}
          </Badge>
        )}
      </Button>

      <button
        ref={sortBtn}
        className={styles.sortBtn}
        onClick={() => setSortAnchor(sortBtn.current?.getBoundingClientRect() ?? null)}
      >
        <ArrowDownUp size={14} />
        <span className={styles.sortLabel}>{sortLabel}</span>
        <span className={styles.caret}>▾</span>
      </button>
      <Popover
        open={!!sortAnchor}
        onClose={() => setSortAnchor(null)}
        anchor={sortAnchor}
        placement="bottom-start"
      >
        <Menu>
          {SORT_OPTIONS.map((opt) => (
            <MenuItem
              key={opt.key}
              leftIcon={opt.key === prefs.sort ? <Check size={14} /> : <span style={{ width: 14 }} />}
              onClick={() => {
                patch({ sort: opt.key });
                setSortAnchor(null);
              }}
            >
              {opt.label}
            </MenuItem>
          ))}
        </Menu>
      </Popover>

      <Tooltip content="Toggle card thumbnails · T">
        <label className={styles.thumbToggle}>
          <ImageIcon size={14} />
          <span className={styles.thumbText}>Thumbs</span>
          <Switch checked={prefs.thumbnails} onChange={(e) => patch({ thumbnails: e.target.checked })} />
        </label>
      </Tooltip>

      <span className={styles.divider} aria-hidden />

      {/* The quick filters take the toolbar's spare room and choose how they
          show by what fits in it. Two hidden copies, named and as icons, are
          what is measured. */}
      <div ref={chipsFit.strip} className={styles.chips} data-fit={chipsFit.fit}>
        {chipsFit.fit === 'menu' ? (
          // Where even the icons would be clipped, one icon button holds them:
          // narrower than the row it stands for.
          <Tooltip content="Quick filters">
            <button
              ref={chipsBtn}
              className={styles.chipsBtn}
              aria-label="Quick filters"
              onClick={() => setChipsAnchor(chipsBtn.current?.getBoundingClientRect() ?? null)}
            >
              <ListFilter size={14} aria-hidden />
              {chipCount > 0 && (
                <Badge variant="solid" style={{ height: 16, padding: '0 5px', fontSize: 10 }}>
                  {chipCount}
                </Badge>
              )}
              <span className={styles.caret} aria-hidden>▾</span>
            </button>
          </Tooltip>
        ) : (
          <>
            <span className={styles.chipsLabel}>Filters:</span>
            {CHIPS.map((c) => (
              <Tooltip
                key={c.key}
                multiline
                content={<><strong>{c.label}</strong><br />{c.does}</>}
              >
                <button
                  className={cn(styles.chip, chipsFit.fit === 'icons' && styles.chipIcon, prefs.quickFilters[c.key] && styles.chipOn)}
                  onClick={() => toggleChip(c.key)}
                  aria-pressed={prefs.quickFilters[c.key]}
                  aria-label={chipsFit.fit === 'icons' ? c.label : undefined}
                >
                  {c.icon}
                  {chipsFit.fit === 'full' && <span>{c.label}</span>}
                </button>
              </Tooltip>
            ))}
          </>
        )}
        <div className={styles.chipsMeasureBox} aria-hidden>
          <div ref={chipsFit.measureFull} className={styles.chipsMeasure}>
            <span className={styles.chipsLabel}>Filters:</span>
            {CHIPS.map((c) => <span key={c.key} className={styles.chip}>{c.icon}<span>{c.label}</span></span>)}
          </div>
          <div ref={chipsFit.measureIcons} className={styles.chipsMeasure}>
            <span className={styles.chipsLabel}>Filters:</span>
            {CHIPS.map((c) => <span key={c.key} className={cn(styles.chip, styles.chipIcon)}>{c.icon}</span>)}
          </div>
        </div>
      </div>
      <Popover
        open={!!chipsAnchor && chipsFit.fit === 'menu'}
        onClose={() => setChipsAnchor(null)}
        anchor={chipsAnchor}
        placement="bottom-start"
        width={240}
      >
        <div className={styles.chipsPopover}>
          {CHIPS.map((c) => (
            <button
              key={c.key}
              className={cn(styles.chip, styles.chipStack, prefs.quickFilters[c.key] && styles.chipOn)}
              onClick={() => toggleChip(c.key)}
              aria-pressed={prefs.quickFilters[c.key]}
            >
              {c.icon}
              <span>{c.label}</span>
              {prefs.quickFilters[c.key] && <Check size={12} style={{ marginLeft: 'auto' }} />}
            </button>
          ))}
        </div>
      </Popover>

      <FilterModal
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        value={filterValue}
        columns={columns}
        ticketTypes={ticketTypes}
        onApply={(next) => patch({
          quickFilters: next.quickFilters,
          assigneeIds: next.assigneeIds,
          authorIds: next.authorIds,
          columnIds: next.columnIds,
          createdFrom: next.createdFrom || undefined,
          createdTo: next.createdTo || undefined,
          // Undefined rather than an empty array when nothing is selected, so a
          // cleared filter stops being sent at all rather than persisting as a
          // filter that matches everything.
          typeIds: next.typeIds?.length ? next.typeIds : undefined,
        })}
      />
    </div>
  );
}

/**
 * Decides how the quick filters show (see ChipsFit). The strip grows into
 * whatever the toolbar's other controls leave, so its own width is the room
 * there is, whatever it holds, and showing the filters another way never
 * changes the room it is decided from.
 */
function useChipsFit() {
  const [fit, setFit] = useState<ChipsFit>('full');
  const fitNow = useRef(fit);
  const strip = useRef<HTMLDivElement>(null);
  const measureFull = useRef<HTMLDivElement>(null);
  const measureIcons = useRef<HTMLDivElement>(null);

  const decide = useCallback(() => {
    const el = strip.current;
    const full = measureFull.current;
    const icons = measureIcons.current;
    if (!el || !full || !icons) return;
    const style = getComputedStyle(el);
    const room = el.clientWidth - (parseFloat(style.paddingLeft) || 0) - (parseFloat(style.paddingRight) || 0);
    // Half a pixel of slack for sub-pixel widths on a scaled display.
    const next: ChipsFit = full.offsetWidth <= room + 0.5 ? 'full'
      : icons.offsetWidth <= room + 0.5 ? 'icons'
        : 'menu';
    if (next !== fitNow.current) {
      fitNow.current = next;
      setFit(next);
    }
  }, []);

  useLayoutEffect(() => {
    const observer = new ResizeObserver(decide);
    for (const el of [strip.current, measureFull.current, measureIcons.current]) {
      if (el) observer.observe(el);
    }
    decide();
    return () => observer.disconnect();
  }, [decide]);

  return { fit, strip, measureFull, measureIcons };
}
